/*
 * Static map (content/component-static-map.php): popups on every screen,
 * plus drag-to-pan and pinch-to-zoom below lg. Each [data-static-map] on the
 * page runs on its own.
 *
 * Markup contract, all written by the PHP components:
 *
 *   [data-static-map]               the window; overflow hidden below lg.
 *                                   data-static-map-zoom / -max-zoom options
 *     [data-static-map-canvas]      the map: <img width height> + markers
 *       [data-static-map-marker]    a marker, placed with left/top in %
 *         [data-static-map-home]    …the one mobile opens centred on
 *         [data-static-map-popup]   its popup, with an id
 *           [data-static-map-popup-box]
 *     [data-static-map-zoom-step]   the +/− buttons ("in" / "out")
 *
 *   [data-static-map-trigger]       anything that opens a popup: the markers
 *                                   themselves and the list entries. Linked by
 *                                   aria-controls="<popup id>", so a trigger
 *                                   can sit anywhere on the page.
 */

// Must match the lg: classes in component-static-map.php — below this the
// map pans; from it up the inline styles are cleared.
const MOBILE = "(width < 64rem)";

// Finger travel, in px, before a touch counts as a drag instead of a tap.
const DRAG_THRESHOLD = 6;

// Each +/− press multiplies or divides the zoom by this.
const BUTTON_STEP = 1.5;

// Room kept around a marker before it counts as out of the window; the extra
// at the top is for its popup.
const EDGE = 30;
const EDGE_TOP = 90;

export default function initStaticMaps() {
  document.querySelectorAll("[data-static-map]").forEach((root) => {
    const canvas = root.querySelector("[data-static-map-canvas]");
    const img = canvas?.querySelector("img");
    if (!canvas || !img) return;

    const pan = setupPan(root, canvas, img);
    setupPopups(root, pan);
  });
}

/*
 * Popups. Clicking a trigger opens its popup; clicking it again, clicking
 * anywhere else or pressing Escape closes it. One popup per map is open at
 * a time.
 *
 * The marker wrappers are translated, and a transform makes each one its own
 * stacking context — so the popup's z-index cannot lift it over a later
 * marker. The open marker's wrapper is raised instead.
 */
function setupPopups(root, pan) {
  let open = null;

  const triggersFor = (popup) => document.querySelectorAll(`[data-static-map-trigger][aria-controls="${popup.id}"]`);

  // Slide the box sideways so it stays inside the window. Only the box
  // moves; the arrow stays centred on the marker.
  function fit(popup) {
    const box = popup.querySelector("[data-static-map-popup-box]");
    box.style.translate = "";

    const rect = box.getBoundingClientRect();
    const bounds = root.getBoundingClientRect();
    let shift = 0;

    if (rect.left < bounds.left) {
      shift = bounds.left - rect.left;
    } else if (rect.right > bounds.right) {
      shift = bounds.right - rect.right;
    }

    box.style.translate = `calc(-50% + ${shift}px) 0`;
  }

  function close() {
    if (!open) return;

    open.closest("[data-static-map-marker]").classList.remove("z-20");
    open.classList.add("hidden");
    triggersFor(open).forEach((el) => el.setAttribute("aria-expanded", "false"));

    open = null;
  }

  function show(popup, fromOutside) {
    close();

    const marker = popup.closest("[data-static-map-marker]");
    marker.classList.add("z-20");
    popup.classList.remove("hidden");
    triggersFor(popup).forEach((el) => el.setAttribute("aria-expanded", "true"));

    // Pan first, so the box is fitted where it will actually sit.
    pan.reveal(marker);
    fit(popup);

    open = popup;

    /*
     * A list entry can be far from the map; bring the marker on screen.
     * Scroll the window, not scrollIntoView(): that would also scroll the
     * overflow-hidden map window and knock the map out of place.
     */
    if (fromOutside) {
      const rect = marker.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        window.scrollBy({
          top: rect.top + rect.height / 2 - window.innerHeight / 2,
          behavior: "smooth",
        });
      }
    }
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-static-map-trigger]");
    const popup = trigger && document.getElementById(trigger.getAttribute("aria-controls"));

    // A trigger for this map: toggle its popup.
    if (popup && root.contains(popup)) {
      if (popup === open) {
        close();
      } else {
        show(popup, !root.contains(trigger));
      }
      return;
    }

    if (!event.target.closest("[data-static-map-popup]")) close();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  window.addEventListener("resize", () => {
    if (open) fit(open);
  });
}

/*
 * Pan and zoom, below lg only.
 *
 * Zoom changes the canvas *width*, not a scale() transform: the markers are
 * placed in percentages, so they spread apart as the map grows while staying
 * their own size. Only the pan is a transform.
 *
 * A tap still reaches the markers. Pointer capture is only taken once a
 * finger has moved past DRAG_THRESHOLD — capturing on pointerdown would
 * retarget the click to the window — and the click that ends a drag is
 * swallowed so it does not open or close a popup.
 *
 * Returns { reveal(marker) }, which pans a marker into the window if it is
 * outside it. It does nothing from lg up.
 */
function setupPan(root, canvas, img) {
  const media = window.matchMedia(MOBILE);
  const ratio = img.getAttribute("height") / img.getAttribute("width");
  const startZoom = parseFloat(root.dataset.staticMapZoom) || 1.5;
  const maxZoom = parseFloat(root.dataset.staticMapMaxZoom) || 4;

  // Canvas width and pan offset in px, and the width as a multiple of cover().
  let width = 0;
  let x = 0;
  let y = 0;
  let zoom = startZoom;

  const pointers = new Map();
  let moved = 0;
  let dragged = false;
  let pinch = null;

  const bounds = () => ({ w: root.clientWidth, h: root.clientHeight });

  // Narrowest width at which the map still fills the window both ways.
  function cover() {
    const { w, h } = bounds();
    return Math.max(w, h / ratio);
  }

  const clampWidth = (value) => Math.min(Math.max(value, cover()), cover() * maxZoom);

  function apply() {
    const { w, h } = bounds();

    width = clampWidth(width);
    x = Math.min(0, Math.max(w - width, x));
    y = Math.min(0, Math.max(h - width * ratio, y));
    zoom = width / cover();

    canvas.style.width = `${width}px`;
    canvas.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  // Zoom to newWidth, keeping the map point under (cx, cy) where it is.
  function zoomAt(newWidth, cx, cy) {
    const px = (cx - x) / width;
    const py = (cy - y) / (width * ratio);

    // Clamp before working out the pan, or a zoom past the limit drifts.
    width = clampWidth(newWidth);
    x = cx - px * width;
    y = cy - py * width * ratio;
    apply();
  }

  function centreOn(marker) {
    const { w, h } = bounds();

    x = w / 2 - (parseFloat(marker.style.left) / 100) * width;
    y = h / 2 - (parseFloat(marker.style.top) / 100) * width * ratio;
    apply();
  }

  function reset() {
    if (!media.matches) {
      canvas.style.width = "";
      canvas.style.transform = "";
      return;
    }

    width = cover() * startZoom;

    const home = canvas.querySelector("[data-static-map-home]");
    if (home) {
      centreOn(home);
    } else {
      x = (bounds().w - width) / 2;
      y = (bounds().h - width * ratio) / 2;
      apply();
    }
  }

  const local = (event) => {
    const rect = root.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  function pinchState() {
    const [a, b] = [...pointers.values()];
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }

  root.addEventListener("pointerdown", (event) => {
    if (!media.matches || event.target.closest("[data-static-map-zoom-step]")) return;

    if (pointers.size === 0) {
      moved = 0;
      dragged = false;
    }

    pointers.set(event.pointerId, local(event));
    if (pointers.size === 2) pinch = pinchState();
  });

  root.addEventListener("pointermove", (event) => {
    const last = pointers.get(event.pointerId);
    if (!last) return;

    const now = local(event);

    // One finger: pan.
    if (pointers.size === 1) {
      moved += Math.hypot(now.x - last.x, now.y - last.y);

      if (!dragged && moved > DRAG_THRESHOLD) {
        dragged = true;
        root.setPointerCapture(event.pointerId);
      }

      if (dragged) {
        x += now.x - last.x;
        y += now.y - last.y;
        apply();
      }

      pointers.set(event.pointerId, now);
      return;
    }

    // Two fingers: follow the midpoint and zoom around it.
    pointers.set(event.pointerId, now);
    dragged = true;

    const next = pinchState();
    x += next.mid.x - pinch.mid.x;
    y += next.mid.y - pinch.mid.y;
    zoomAt(width * (next.distance / pinch.distance), next.mid.x, next.mid.y);
    pinch = next;
  });

  const release = (event) => {
    pointers.delete(event.pointerId);
    pinch = pointers.size === 2 ? pinchState() : null;
  };

  root.addEventListener("pointerup", release);
  root.addEventListener("pointercancel", release);

  // Capture phase, so the click ending a drag never reaches setupPopups().
  root.addEventListener("click", (event) => {
    if (!dragged) return;

    event.preventDefault();
    event.stopPropagation();
    dragged = false;
  }, true);

  root.querySelectorAll("[data-static-map-zoom-step]").forEach((button) => {
    button.addEventListener("click", (event) => {
      // Not a map tap — leave any open popup alone.
      event.stopPropagation();

      const { w, h } = bounds();
      const step = button.dataset.staticMapZoomStep === "in" ? BUTTON_STEP : 1 / BUTTON_STEP;
      zoomAt(width * step, w / 2, h / 2);
    });
  });

  media.addEventListener("change", reset);

  // Orientation changes resize the window; keep the zoom, re-clamp the pan.
  window.addEventListener("resize", () => {
    if (!media.matches) return;

    width = cover() * zoom;
    apply();
  });

  reset();

  return {
    reveal(marker) {
      if (!media.matches) return;

      const rect = marker.getBoundingClientRect();
      const frame = root.getBoundingClientRect();

      const inside =
        rect.left >= frame.left + EDGE &&
        rect.right <= frame.right - EDGE &&
        rect.top >= frame.top + EDGE_TOP &&
        rect.bottom <= frame.bottom - EDGE;

      if (!inside) centreOn(marker);
    },
  };
}
